import { Component, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { HeaderNewComponent } from '../../_components/header-new/header-new.component';
import { RecaptchaModule } from 'ng-recaptcha-2';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  FormBuilder,
} from '@angular/forms';
import { AlertService } from '../../_services/utils/alert.service';
import { CustomerMessage } from '../../_interfaces/customer-message';
import { customerMessageTypes } from '../../_constants/constants';
import { ContactService } from '../../_services/contact.service';
import { FooterComponent } from "../../_components/footer/footer.component";
import { Meta, Title } from '@angular/platform-browser';
import { TrustboxComponent } from "../../_components/trustbox/trustbox.component";
import { NavbarComponent } from "../../V4/_components/navbar/navbar.component";

@Component({
    selector: 'app-contact',
    imports: [
        HeaderNewComponent,
        RecaptchaModule,
        ReactiveFormsModule,
        NgClass,
        NavbarComponent,
        FooterComponent,
        TrustboxComponent
    ],
    templateUrl: './contact.component.html',
    styleUrl: './contact.component.scss'
})
export class ContactComponent implements OnInit {
  captchaResolved: boolean = false;
  captchaResponse: string = '';
  captchaEnabled: boolean = true;

  generalContactFrom: FormGroup = new FormGroup({
    name: new FormControl(''),
    email: new FormControl(''),
    message: new FormControl(''),
  });

  getQuoteFrom: FormGroup = new FormGroup({
    name: new FormControl(''),
    email: new FormControl(''),
    companyName: new FormControl(''),
    phoneNumber: new FormControl(''),
    productRequired: new FormControl(''),
    coverageRequired: new FormControl(''),
    additionQuestions: new FormControl(''),
    deliveryPostcode: new FormControl(''),
  });

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private contactService: ContactService,
    private meta: Meta,   
    private title: Title
  ) {}

  ngOnInit(): void {
    this.generalContactFrom = this.formBuilder.group({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
      ]),
      email: new FormControl('', [Validators.required, Validators.email]),
      message: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(1000),
      ]),
    });
    this.getQuoteFrom = this.formBuilder.group({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
      ]),
      email: new FormControl('', [Validators.required, Validators.email]),
      phoneNumber: new FormControl('', [
        Validators.required,
        Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$'),
      ]),
      companyName: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      productRequired: new FormControl('', [
        Validators.minLength(10),
        Validators.maxLength(300),
      ]),
      coverageRequired: new FormControl('', [Validators.min(0)]),
      additionQuestions: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(400),
      ]),
      deliveryPostcode: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(50),
      ]),
    });
    this.title.setTitle("Contact Us | Customer Support & Advice | Nustone");
    this.meta.addTag({name: 'description', 'content' : "Need to get in touch? You can reach us on 01206 700 599 or via email at support@nustone.co.uk. We would be happy to help!"});
    this.meta.addTag({ name: 'og:title', content:"Contact Us | Customer Support & Advice | Nustone"})
    this.meta.addTag({ name: 'og:description', content:"Need to get in touch? You can reach us on 01206 700 599 or via email at support@nustone.co.uk. We would be happy to help!"})
  }

  // This is needed for the reactive forms, I don't know why
  get f(): { [key: string]: AbstractControl } {
    return this.generalContactFrom.controls;
  }
  get fs(): { [key: string]: AbstractControl } {
    return this.getQuoteFrom.controls;
  }

  onContactFormSubmit() {
    if (this.generalContactFrom.invalid) {
      this.alertService.alert('Please provide valid data!', 'error');
      return;
    }

    const formData = this.generalContactFrom.value;

    let paramData: CustomerMessage = {
      name: formData.name,
      email: formData.email.toLowerCase(),
      message: formData.message,
      type: customerMessageTypes.GENERAL,
    };

    if (this.captchaEnabled && this.captchaResolved)
      paramData.captchaToken = this.captchaResponse;

    this.sendStorCustomerMessage(paramData);
  }

  onGetQuoteFormSubmit() {
    if (this.getQuoteFrom.invalid) {
      this.alertService.alert('Please provide valid data!', 'error');
      return;
    }

    const formData = this.getQuoteFrom.value;
    const additionalData = {
      companyName: formData.companyName ? formData.companyName : '-',
      phoneNumber: formData.phoneNumber,
      productRequired: formData.productRequired
        ? formData.productRequired
        : '-',
      coverageRequired: formData.coverageRequired
        ? formData.coverageRequired
        : '-',
      additionQuestions: formData.additionQuestions
        ? formData.additionQuestions
        : '-',
      deliveryPostcode: formData.deliveryPostcode,
    };

    let paramData: CustomerMessage = {
      name: formData.name,
      email: formData.email.toLowerCase(),
      message: 'Please give me a quote for following details',
      type: customerMessageTypes.QUOTE,
      additionalData: JSON.stringify(additionalData),
    };

    if (this.captchaEnabled && this.captchaResolved)
      paramData.captchaToken = this.captchaResponse;

    this.sendStorCustomerMessage(paramData);
  }

  onCaptchaResolved(response: any) {
    this.captchaResponse = response;
    this.captchaResolved = !!response;
  }

  private async sendStorCustomerMessage(paramData: CustomerMessage) {
    await this.contactService
      .sendStoreCustomerMessageToAdmin(paramData)
      .subscribe({
        next: (response) => {
          this.alertService.alert('Your message has been received!', 'success');
          this.generalContactFrom.reset();
          this.getQuoteFrom.reset();
        },
        error: (error) => {
          this.alertService.alert(error.error.message, 'error');
        },
      });
  }
}
